import { uid } from '../lib/cn'
import { proximoDia } from '../lib/geo'
import type {
  Categoria,
  Cliente,
  Gasto,
  LineaVenta,
  Lote,
  MetodoPago,
  Movimiento,
  Producto,
  TipoGasto,
  Unidad,
  Usuario,
  Venta,
} from '../types'
import { AJUSTES_POR_DEFECTO, snapshotVacio, type Snapshot } from './adapter'

/** Datos de demostración. Sirven para que la app tenga contenido real
 *  desde el primer arranque; se borran completos desde Ajustes. */

const ahora = () => new Date().toISOString()
const hace = (dias: number, hora = 10) => {
  const d = new Date()
  d.setDate(d.getDate() - dias)
  d.setHours(hora, Math.floor(Math.random() * 60), 0, 0)
  return d.toISOString()
}
const enDias = (dias: number) => {
  const d = new Date()
  d.setDate(d.getDate() + dias)
  return d.toISOString().slice(0, 10)
}
const elegir = <T,>(a: T[]) => a[Math.floor(Math.random() * a.length)]
const entre = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min

export const USUARIOS_DEMO: Usuario[] = [
  {
    id: 'u_admin',
    nombre: 'Felipe Arriagada',
    email: 'admin@novaberry.cl',
    telefono: '+56 9 8123 4567',
    rol: 'admin',
    activo: true,
    comision: 0,
    creado: hace(240),
  },
  {
    id: 'u_caro',
    nombre: 'Caro Godoy',
    rol: 'vendedor',
    activo: true,
    comision: 30,
    creado: hace(180),
  },
  {
    id: 'u_felipedi',
    nombre: 'Felipe Díaz',
    rol: 'vendedor',
    activo: true,
    comision: 30,
    creado: hace(150),
  },
  {
    id: 'u_nicolas',
    nombre: 'Nicolás Bravo',
    rol: 'vendedor',
    activo: true,
    comision: 30,
    creado: hace(120),
  },
  {
    id: 'u_marco',
    nombre: 'Marco Bravo',
    rol: 'vendedor',
    activo: true,
    comision: 30,
    creado: hace(90),
  },
]

interface SemillaProducto {
  codigo: string
  nombre: string
  categoria: Categoria
  peso: number
  unidad: Unidad
  costo: number
  sugerido: number
  stock: number
  foto?: string
  /** Posición fija en Inventario — el orden que pidió el dueño. */
  orden: number
  /** false = todavía no se vende (falta foto, precio, o no está listo). */
  activo: boolean
}

// Precios de costo y venta reales (agosto 2026), igual que en la base
// de producción. Los que no tienen precio de venta confirmado quedan
// "activo: false" hasta que se sepa a cuánto se venden.
const PRODUCTOS: SemillaProducto[] = [
  { codigo: 'AJO-NEG', nombre: 'Ajo negro', categoria: 'otros', peso: 2, unidad: 'unidad', costo: 800, sugerido: 2900, stock: 0, foto: '/productos/ajo-negro.png', orden: 10, activo: true },
  { codigo: 'FRA-CON', nombre: 'Frambuesa congelada', categoria: 'frutas', peso: 1, unidad: 'kg', costo: 5600, sugerido: 7990, stock: 88, foto: '/productos/frambuesas-congelado.png', orden: 20, activo: true },
  { codigo: 'FRU-CON', nombre: 'Frutilla congelada', categoria: 'frutas', peso: 1, unidad: 'kg', costo: 3600, sugerido: 4490, stock: 10, foto: '/productos/frutillas-congelado.png', orden: 30, activo: true },
  { codigo: 'ARA-CON', nombre: 'Arándano congelado', categoria: 'frutas', peso: 1, unidad: 'kg', costo: 4200, sugerido: 5990, stock: 88, foto: '/productos/arandanos-congelado.png', orden: 40, activo: true },
  { codigo: 'MIX-KG', nombre: 'Mix berries', categoria: 'frutas', peso: 1, unidad: 'kg', costo: 4590, sugerido: 8590, stock: 58, foto: '/productos/mix-berries.png', orden: 50, activo: true },
  { codigo: 'MIE-400', nombre: 'Miel', categoria: 'miel', peso: 400, unidad: 'g', costo: 1520, sugerido: 2900, stock: 45, foto: '/productos/miel.png', orden: 60, activo: true },
  { codigo: 'AVE-1K', nombre: 'Avena integral', categoria: 'avena', peso: 1, unidad: 'kg', costo: 800, sugerido: 1890, stock: 180, foto: '/productos/avena.png', orden: 70, activo: true },
  { codigo: 'DAT-500', nombre: 'Dátiles', categoria: 'otros', peso: 500, unidad: 'g', costo: 1750, sugerido: 3990, stock: 40, foto: '/productos/datiles.png', orden: 80, activo: true },
  { codigo: 'MAR-KG', nombre: 'Maracuyá con pepa', categoria: 'frutas', peso: 1, unidad: 'kg', costo: 5300, sugerido: 6890, stock: 5, orden: 90, activo: true },
  // El resto: activos igual, pero con precio de venta provisorio hasta
  // que llegue el real — por eso van al final de la lista.
  { codigo: 'ARA-KG', nombre: 'Arándano fresco', categoria: 'frutas', peso: 1, unidad: 'kg', costo: 3800, sugerido: 5700, stock: 0, foto: '/productos/arandanos.png', orden: 100, activo: true },
  { codigo: 'FRA-KG', nombre: 'Frambuesa fresca', categoria: 'frutas', peso: 1, unidad: 'kg', costo: 4500, sugerido: 6800, stock: 0, foto: '/productos/frambuesas.png', orden: 101, activo: true },
  { codigo: 'FRU-KG', nombre: 'Frutilla fresca', categoria: 'frutas', peso: 1, unidad: 'kg', costo: 3200, sugerido: 4800, stock: 10, foto: '/productos/frutillas.png', orden: 102, activo: false },
  { codigo: 'MOR-CON', nombre: 'Morón congelado', categoria: 'frutas', peso: 1, unidad: 'kg', costo: 3900, sugerido: 5200, stock: 34, foto: '/productos/moras-congelado.png', orden: 103, activo: true },
  { codigo: 'MOR-KG', nombre: 'Morón fresco', categoria: 'frutas', peso: 1, unidad: 'kg', costo: 2500, sugerido: 3800, stock: 0, foto: '/productos/moras.png', orden: 104, activo: true },
  { codigo: 'AJO-CHI', nombre: 'Ajo chilote', categoria: 'otros', peso: 1, unidad: 'kg', costo: 5000, sugerido: 7500, stock: 0, orden: 105, activo: true },
  { codigo: 'CRU-FRA', nombre: 'Frambuesa Crumble', categoria: 'otros', peso: 1, unidad: 'kg', costo: 4800, sugerido: 7200, stock: 5, orden: 106, activo: false },
]

const EMPRESAS: [string, Cliente['rubro'], string, string, string][] = [
  ['Pastelería Dulce Vida', 'pasteleria', '76.412.880-1', 'Av. Providencia 2134', 'Providencia'],
  ['Restaurante La Cava', 'restaurante', '77.203.115-9', 'Isidora Goyenechea 3120', 'Las Condes'],
  ['Café Aroma Norte', 'cafeteria', '78.660.442-K', 'Manuel Montt 890', 'Ñuñoa'],
  ['Hotel Los Cerros', 'hotel', '96.884.310-2', 'Camino El Alba 11000', 'Vitacura'],
  ['Distribuidora Sur Fresh', 'distribuidor', '79.115.007-3', 'Ruta 5 Sur km 12', 'San Bernardo'],
  ['Pastelería Migas', 'pasteleria', '77.998.221-6', 'Av. Matta 1450', 'Santiago'],
  ['Bistró Verde', 'restaurante', '76.550.909-8', 'Av. Italia 1360', 'Providencia'],
  ['Café Trece Granos', 'cafeteria', '78.321.664-4', 'Bellavista 077', 'Recoleta'],
]

const PARTICULARES: [string, string, string, string][] = [
  ['María', 'González', 'Los Alerces 445', 'Maipú'],
  ['Jorge', 'Ramírez', 'Pasaje Las Rosas 12', 'La Florida'],
  ['Camila', 'Fuentes', 'Av. Kennedy 5800', 'Las Condes'],
  ['Sebastián', 'Vera', 'El Roble 230', 'Puente Alto'],
  ['Antonia', 'Rojas', 'Los Aromos 78', 'Ñuñoa'],
  ['Pablo', 'Herrera', 'San Martín 1120', 'Santiago'],
]

function generarProductos(): { productos: Producto[]; lotes: Lote[] } {
  const productos: Producto[] = []
  const lotes: Lote[] = []

  PRODUCTOS.forEach(({ codigo, nombre, categoria, peso, unidad, costo, sugerido, stock, foto, orden, activo }, i) => {
    const id = `p_${codigo.toLowerCase().replace(/[^a-z0-9]/g, '')}`
    productos.push({
      id,
      codigo,
      nombre,
      categoria,
      peso,
      unidad,
      precioCosto: costo,
      precioSugerido: sugerido,
      stock,
      reservado: stock > 0 && i % 5 === 0 ? entre(2, Math.min(8, stock)) : 0,
      stockMinimo: null,
      foto,
      ordenCatalogo: orden,
      codigoBarras: `78${String(100000 + i * 137).padStart(11, '0')}`,
      activo,
      creado: hace(200 - i),
    })

    // Los perecibles llevan lote con vencimiento; los secos no lo necesitan.
    if (stock > 0) {
      const perecible = categoria === 'frutas'
      lotes.push({
        id: uid('l'),
        productoId: id,
        codigo: `L${new Date().getFullYear()}-${String(i + 1).padStart(3, '0')}`,
        cantidad: stock,
        cantidadInicial: stock,
        vencimiento: perecible ? enDias(entre(3, 45)) : enDias(entre(120, 400)),
        costo,
        proveedor: elegir(['Agrícola Los Ríos', 'Campo Nevado', 'Fundo El Peral', 'Apícola del Sur']),
        ingresado: hace(entre(2, 25)),
      })
    }
  })

  return { productos, lotes }
}

function generarClientes(): Cliente[] {
  const clientes: Cliente[] = []
  EMPRESAS.forEach(([nombre, rubro, rut, direccion, comuna], i) => {
    clientes.push({
      id: `c_emp_${i}`,
      tipo: 'empresa',
      nombre,
      rubro,
      rut,
      direccion,
      comuna,
      telefono: `+56 2 2${entre(200, 899)} ${entre(1000, 9999)}`,
      email: `contacto@${nombre.toLowerCase().replace(/[^a-z]/g, '')}.cl`,
      contacto: elegir(['Andrea Pérez', 'Luis Cárdenas', 'Paula Soto', 'Marcelo Díaz', 'Ignacia Reyes']),
      observaciones: i % 3 === 0 ? 'Entrega antes de las 09:00.' : '',
      creado: hace(150 - i * 8),
    })
  })
  PARTICULARES.forEach(([nombre, apellido, direccion, comuna], i) => {
    clientes.push({
      id: `c_par_${i}`,
      tipo: 'particular',
      nombre,
      apellido,
      direccion,
      comuna,
      telefono: `+56 9 ${entre(4000, 9999)} ${entre(1000, 9999)}`,
      email: `${nombre.toLowerCase()}.${apellido.toLowerCase()}@correo.cl`,
      creado: hace(120 - i * 10),
    })
  })
  return clientes
}

function generarVentas(productos: Producto[], clientes: Cliente[], usuarios: Usuario[]) {
  const ventas: Venta[] = []
  const movimientos: Movimiento[] = []
  const vendedores = usuarios.filter((u) => u.rol !== 'admin' || u.id === 'u_admin')
  const metodos: MetodoPago[] = ['efectivo', 'transferencia', 'transferencia', 'tarjeta', 'credito']
  let folio = 1000

  // 90 días de historial: suficiente para que los gráficos de mes y año
  // muestren tendencia real.
  for (let dia = 89; dia >= 0; dia--) {
    const esFinde = [0, 6].includes(new Date(Date.now() - dia * 86_400_000).getDay())
    const cantidadVentas = esFinde ? entre(0, 2) : entre(1, 5)

    for (let v = 0; v < cantidadVentas; v++) {
      const cliente = elegir(clientes)
      const usuario = elegir(vendedores)
      const nLineas = cliente.tipo === 'empresa' ? entre(1, 4) : entre(1, 2)
      const usados = new Set<string>()
      const lineas: LineaVenta[] = []

      for (let l = 0; l < nLineas; l++) {
        const prod = elegir(productos)
        if (usados.has(prod.id)) continue
        usados.add(prod.id)
        const cantidad = cliente.tipo === 'empresa' ? entre(4, 30) : entre(1, 4)
        // Las empresas compran con descuento por volumen.
        const descuento = cliente.tipo === 'empresa' ? entre(0, 12) / 100 : 0
        const precioUnitario = Math.round((prod.precioSugerido * (1 - descuento)) / 10) * 10
        lineas.push({
          productoId: prod.id,
          nombre: prod.nombre,
          codigo: prod.codigo,
          cantidad,
          precioUnitario,
          costoUnitario: prod.precioCosto,
          subtotal: precioUnitario * cantidad,
        })
      }
      if (!lineas.length) continue

      const total = lineas.reduce((s, l) => s + l.subtotal, 0)
      const costoTotal = lineas.reduce((s, l) => s + l.costoUnitario * l.cantidad, 0)
      const metodo = elegir(metodos)
      const fecha = hace(dia, entre(9, 19))
      const id = uid('v')
      const estado =
        dia > 3 ? 'entregada' : elegir(['pendiente', 'preparando', 'despachada', 'entregada'] as const)

      ventas.push({
        id,
        folio: `V-${++folio}`,
        clienteId: cliente.id,
        clienteNombre: [cliente.nombre, cliente.apellido].filter(Boolean).join(' '),
        clienteTipo: cliente.tipo,
        usuarioId: usuario.id,
        usuarioNombre: usuario.nombre,
        lineas,
        total,
        costoTotal,
        utilidad: total - costoTotal,
        comision: Math.round((total - costoTotal) * ((usuario.comision || 0) / 100)),
        metodoPago: metodo,
        estado,
        pagado: metodo !== 'credito' || dia > 20,
        vencimientoPago: metodo === 'credito' ? enDias(30 - dia) : undefined,
        fechaEntrega: estado === 'entregada' ? fecha : undefined,
        // Para que Despachos tenga con qué mostrar: lo que sigue en
        // camino queda con su día de reparto.
        diaDespacho: estado === 'despachada' ? elegir(['martes', 'jueves'] as const) : undefined,
        fechaDespacho: estado === 'despachada' ? proximoDia(elegir([2, 4] as const)) : undefined,
        fecha,
      })

      for (const l of lineas) {
        movimientos.push({
          id: uid('m'),
          productoId: l.productoId,
          tipo: 'venta',
          cantidad: -l.cantidad,
          stockResultante: 0, // histórico sintético; el real se calcula al vender
          referenciaId: id,
          usuarioId: usuario.id,
          usuarioNombre: usuario.nombre,
          fecha,
        })
      }
    }
  }

  return { ventas, movimientos }
}

// Gastos fijos que se repiten cada mes, más algunos puntuales, para
// que la pantalla de Gastos y el gráfico de rentabilidad no arranquen
// vacíos —incluye parte del año pasado, para que la comparación anual
// tenga con qué comparar.
const GASTOS_FIJOS: [string, TipoGasto, number][] = [
  ['Arriendo bodega', 'fijo', 450000],
  ['Sueldos equipo', 'fijo', 1200000],
  ['Luz y agua', 'fijo', 85000],
  ['Internet y telefonía', 'fijo', 35000],
]

function generarGastos(): Gasto[] {
  const gastos: Gasto[] = []
  for (let mesesAtras = 15; mesesAtras >= 0; mesesAtras--) {
    const d = new Date()
    d.setDate(5)
    d.setMonth(d.getMonth() - mesesAtras)
    const fechaMes = d.toISOString()

    for (const [descripcion, tipo, monto] of GASTOS_FIJOS) {
      gastos.push({
        id: uid('g'),
        categoria: descripcion.includes('Sueldos')
          ? 'Sueldos'
          : descripcion.includes('Arriendo')
            ? 'Arriendo'
            : 'Servicios básicos',
        tipo,
        descripcion,
        monto: Math.round(monto * (0.95 + Math.random() * 0.1)),
        afectoIva: descripcion !== 'Sueldos equipo',
        usuarioId: 'u_admin',
        usuarioNombre: 'Felipe Arriagada',
        fecha: fechaMes,
      })
    }

    // Publicidad no todos los meses —así se ve variado, no una línea recta.
    if (mesesAtras % 2 === 0) {
      const d2 = new Date(d)
      d2.setDate(entre(8, 22))
      gastos.push({
        id: uid('g'),
        categoria: 'Publicidad',
        tipo: 'publicidad',
        descripcion: 'Campaña Instagram y Facebook Ads',
        monto: entre(60000, 180000),
        afectoIva: true,
        proveedor: 'Meta',
        usuarioId: 'u_admin',
        usuarioNombre: 'Felipe Arriagada',
        fecha: d2.toISOString(),
      })
    }
  }
  return gastos
}

export function construirSemilla(): Snapshot {
  const base = snapshotVacio()
  const { productos, lotes } = generarProductos()
  const clientes = generarClientes()
  const { ventas, movimientos } = generarVentas(productos, clientes, USUARIOS_DEMO)

  const ingresos: Movimiento[] = lotes.map((lote) => {
    const p = productos.find((x) => x.id === lote.productoId)!
    return {
      id: uid('m'),
      productoId: lote.productoId,
      tipo: 'ingreso',
      cantidad: lote.cantidadInicial,
      stockResultante: p.stock,
      loteId: lote.id,
      proveedor: lote.proveedor,
      costo: lote.costo,
      usuarioId: 'u_admin',
      usuarioNombre: 'Felipe Arriagada',
      observaciones: 'Ingreso inicial de temporada',
      fecha: lote.ingresado,
    }
  })

  return {
    ...base,
    usuarios: USUARIOS_DEMO,
    productos,
    lotes,
    clientes,
    ventas: ventas.sort((a, b) => b.fecha.localeCompare(a.fecha)),
    movimientos: [...ingresos, ...movimientos].sort((a, b) => b.fecha.localeCompare(a.fecha)),
    gastos: generarGastos().sort((a, b) => b.fecha.localeCompare(a.fecha)),
    bitacora: [
      {
        id: uid('b'),
        usuarioId: 'u_admin',
        usuarioNombre: 'Felipe Arriagada',
        accion: 'crear',
        entidad: 'sistema',
        detalle: 'Se cargaron los datos de demostración.',
        fecha: ahora(),
      },
    ],
    ajustes: { ...AJUSTES_POR_DEFECTO },
  }
}
